import React, { useState, useEffect } from 'react';

const DOMAIN_IDS = ['D1','D2','D3','D4','D5','D6'];

const SCORE_LABELS = [
  { value: 1, label: 'Initial',    color: '#C62828', desc: 'No controls, ad hoc' },
  { value: 2, label: 'Developing', color: '#F57F17', desc: 'Exists but inconsistent' },
  { value: 3, label: 'Defined',    color: '#1565C0', desc: 'Documented, consistently applied' },
  { value: 4, label: 'Managed',    color: '#2E7D32', desc: 'Measured and monitored' },
  { value: 5, label: 'Optimized',  color: '#0078D4', desc: 'Automated, continuously improved' },
];

const scoreColor = s => (SCORE_LABELS.find(l => l.value === s) || {}).color || '#B0BAC5';

export default function Interviews({ API, domains }) {
  const [domain, setDomain] = useState('D1');
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState({});
  const [savedAll, setSavedAll] = useState(false);
  const [savedControls, setSavedControls] = useState({});
  const [dirtyControls, setDirtyControls] = useState({});
  const [expandedGuidance, setExpandedGuidance] = useState({});

  useEffect(() => {
    fetch(`${API}/api/interviews/${domain}`)
      .then(r => r.json()).then(d => {
        setData(d);
        const init = {};
        d.questions.forEach(q => {
          init[q.control_id] = { score: q.current_score||'', notes: q.current_notes||'' };
        });
        setResponses(init);
        setSavedControls({});
        setDirtyControls({});
        setExpandedGuidance({});
      });
    setSavedAll(false);
  }, [domain, API]);

  const update = (id, field, val) => {
    setResponses(p => ({...p, [id]:{...p[id],[field]:val}}));
    setDirtyControls(p => ({...p, [id]: true}));
    setSavedControls(p => ({...p, [id]: false}));
  };

  const saveOne = async (controlId) => {
    const r = responses[controlId];
    if (!r) return;
    await fetch(`${API}/api/interviews/${controlId}`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({score: r.score ? parseInt(r.score) : null, assessor_notes: r.notes})
    });
    setSavedControls(p => ({...p, [controlId]: true}));
    setDirtyControls(p => ({...p, [controlId]: false}));
    setTimeout(() => setSavedControls(p => ({...p, [controlId]: false})), 3000);
  };

  const saveAll = async () => {
    if (!data) return;
    await Promise.all(data.questions.map(q => {
      const r = responses[q.control_id];
      if (!r) return Promise.resolve();
      return fetch(`${API}/api/interviews/${q.control_id}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({score: r.score ? parseInt(r.score) : null, assessor_notes: r.notes})
      });
    }));
    setSavedAll(true);
    setDirtyControls({});
    setSavedControls({});
    setTimeout(() => setSavedAll(false), 3000);
  };

  const domainName = domains.find(d => d.id === domain)?.name || '';
  const progress = data ? data.questions.filter(q => responses[q.control_id]?.score).length : 0;
  const totalQuestions = data ? data.questions.length : 0;
  const avgScore = progress > 0
    ? (data.questions.reduce((sum, q) => {
        const s = responses[q.control_id]?.score;
        return sum + (s ? parseInt(s) : 0);
      }, 0) / progress).toFixed(1)
    : '—';

  return (
    <div>
      {/* Domain tabs */}
      <div className="domain-tabs">
        {DOMAIN_IDS.map(id => {
          const d = domains.find(x => x.id === id);
          return (
            <div key={id} className={`domain-tab${domain===id?' active':''}`} onClick={() => setDomain(id)}>
              {id}
            </div>
          );
        })}
      </div>

      {data && (
        <div className="interview-wrap">
          <div className="interview-domain-bar">
            <div>
              <div style={{fontWeight:600,fontSize:13}}>{domain} — {domainName}</div>
              <div style={{fontSize:11,color:'rgba(255,255,255,0.5)',marginTop:2}}>
                {totalQuestions} interview questions &nbsp;·&nbsp; {progress}/{totalQuestions} scored
                {progress > 0 && <span> &nbsp;·&nbsp; avg {avgScore}</span>}
              </div>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {savedAll && <span style={{fontSize:11,color:'#4CAF50',background:'rgba(76,175,80,0.15)',padding:'3px 10px',borderRadius:10}}>All saved</span>}
              <button className="btn-primary" onClick={saveAll}>Save all</button>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{padding:'0 16px',paddingTop:8}}>
            <div style={{height:4,background:'rgba(255,255,255,0.15)',borderRadius:2,overflow:'hidden'}}>
              <div style={{
                width: totalQuestions > 0 ? `${(progress/totalQuestions)*100}%` : '0%',
                height:'100%',background:'#4CAF50',borderRadius:2,transition:'width 0.3s'
              }}/>
            </div>
          </div>

          {data.questions.map((q, i) => {
            const resp = responses[q.control_id] || {};
            const isSaved = savedControls[q.control_id];
            const isDirty = dirtyControls[q.control_id];
            const isGuidanceOpen = expandedGuidance[q.control_id];
            const selectedScore = resp.score ? parseInt(resp.score) : null;

            return (
            <div className="q-item" key={q.control_id} style={{position:'relative'}}>
              <div className="q-header">
                <span className="q-id">{q.control_id}</span>
                <div className="q-text">{q.question}</div>
                {/* Per-question save */}
                <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center'}}>
                  {isSaved && <span style={{fontSize:9,color:'#4CAF50'}}>Saved</span>}
                  {isDirty && (
                    <button
                      onClick={() => saveOne(q.control_id)}
                      style={{
                        fontSize:9,padding:'3px 10px',background:'#1565C0',color:'#fff',
                        border:'none',borderRadius:4,cursor:'pointer'
                      }}>
                      Save
                    </button>
                  )}
                </div>
              </div>

              {/* Probe text */}
              {q.probe && (
                <div className="q-probe" style={{fontStyle:'italic',color:'#7A8899',fontSize:10,margin:'4px 0 8px 0',lineHeight:1.4}}>
                  <span style={{fontWeight:500,color:'#455A64'}}>Follow-up probe:</span> {q.probe}
                </div>
              )}

              {/* Scoring guidance toggle */}
              {q.scoring_guidance && Object.keys(q.scoring_guidance).length > 0 && (
                <div style={{marginBottom:8}}>
                  <button
                    onClick={() => setExpandedGuidance(p => ({...p, [q.control_id]: !p[q.control_id]}))}
                    style={{
                      fontSize:9,color:'#1565C0',background:'#E3F2FD',border:'1px solid #BBDEFB',
                      borderRadius:4,padding:'3px 10px',cursor:'pointer'
                    }}>
                    {isGuidanceOpen ? '▼ Hide' : '▶ Show'} scoring rubric
                  </button>
                  {isGuidanceOpen && (
                    <div style={{marginTop:6,background:'#F8F9FA',borderRadius:6,padding:'8px 12px',border:'1px solid #E8ECF0'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                        <tbody>
                          {Object.entries(q.scoring_guidance).map(([level, text]) => {
                            const lvl = parseInt(level);
                            const isSelected = selectedScore === lvl;
                            return (
                              <tr key={level} style={{
                                background: isSelected ? `${scoreColor(lvl)}10` : 'transparent',
                                borderLeft: isSelected ? `3px solid ${scoreColor(lvl)}` : '3px solid transparent'
                              }}>
                                <td style={{padding:'4px 8px',fontWeight:600,color:scoreColor(lvl),width:24,verticalAlign:'top'}}>{level}</td>
                                <td style={{padding:'4px 8px',color:'#455A64',lineHeight:1.4}}>{text}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Sub-controls */}
              {(q.sub_controls||[]).length > 0 && (
                <div style={{margin:'8px 0',padding:'8px 12px',background:'#F0F6FF',borderRadius:6,borderLeft:'3px solid #0078D4'}}>
                  <div style={{fontSize:9,color:'#0078D4',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>Sub-controls to assess with {q.control_id}</div>
                  {q.sub_controls.map(sc => (
                    <div key={sc.id} style={{marginBottom:6}}>
                      <div style={{fontSize:10,fontWeight:600,color:'#0078D4'}}>{sc.id}</div>
                      <div style={{fontSize:10,color:'#455A64'}}>{sc.statement}</div>
                      {sc.evidence_required && (
                        <div style={{fontSize:9,color:'#7A8899'}}>Evidence: {sc.evidence_required}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="q-inputs">
                <div>
                  <div style={{fontSize:10,color:'#7A8899',marginBottom:4}}>Maturity score (1-5)</div>
                  <select
                    value={resp.score||''}
                    onChange={e => update(q.control_id,'score',e.target.value)}>
                    <option value="">-- Not scored --</option>
                    {SCORE_LABELS.map(sl => (
                      <option key={sl.value} value={sl.value}>{sl.value} — {sl.label}: {sl.desc}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:10,color:'#7A8899',marginBottom:4}}>Assessor notes</div>
                  <textarea
                    value={resp.notes||''}
                    onChange={e => update(q.control_id,'notes',e.target.value)}
                    placeholder="Document evidence reviewed, interviewee responses, gaps observed..."/>
                </div>
              </div>
            </div>
            );
          })}

          <div style={{padding:'12px 16px',background:'#F8F9FA',display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button className="btn-secondary" onClick={() => {
              const curr = DOMAIN_IDS.indexOf(domain);
              if (curr < DOMAIN_IDS.length-1) setDomain(DOMAIN_IDS[curr+1]);
            }}>Next domain</button>
            <button className="btn-primary" onClick={saveAll}>Save all</button>
          </div>
        </div>
      )}
    </div>
  );
}
